import { useState } from "react";
import type { AnnualRow, Station } from "../lib/types";
import { TROPHIC } from "../lib/trophic";
import { formatValue, plural } from "@core/lib/format";
import ShareCard from "@core/components/ShareCard";

interface Props {
  station: Station;
  rows: AnnualRow[];
  url: string;
  lakeLink: string;
  onClose: () => void;
  onLake: (lake: string) => void;
}

export default function StationDetail({
  station, rows, url, lakeLink, onClose, onLake,
}: Props) {
  const [imgFailed, setImgFailed] = useState(false);
  const t = TROPHIC[station.trophic];

  const trend = station.secchiTrendPerDecade;
  // A tenth of a foot per decade is well inside year-to-year noise; calling
  // that a trend would be overclaiming.
  const trendWord =
    trend === null ? null
      : Math.abs(trend) < 0.5 ? "about steady"
        : trend > 0 ? "clearer" : "less clear";

  return (
    <aside className="detail" aria-label={`Details for ${station.siteId}`}>
      <header className="detail-head">
        <span className="detail-id">
          <i className="detail-dot" style={{ background: t.color }} />
          {station.siteId}
        </span>
        <button className="icon-btn" onClick={onClose} aria-label="Close station details">
          <svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true">
            <path d="M3 3l10 10M13 3L3 13" stroke="currentColor" strokeWidth="1.8"
              strokeLinecap="round" fill="none" />
          </svg>
        </button>
      </header>

      <h2 className="detail-name">{station.name}</h2>
      {station.ambiguousId && (
        <p className="detail-warn">
          Station ID <b>{station.siteId}</b> is used by more than one record in
          Airtable. This link addresses this record specifically.
        </p>
      )}
      <p className="detail-sub">
        <a
          href={lakeLink}
          onClick={(e) => { e.preventDefault(); onLake(station.lake); }}
        >
          {station.lake}
        </a>
        {station.years > 0 && ` · ${plural(station.years, "year")} of record`}
        {station.firstYear && station.lastYear && ` (${station.firstYear}–${station.lastYear})`}
      </p>

      {station.imageUrl && !imgFailed && (
        <figure className="shot">
          <img
            src={station.imageUrl}
            alt={`Monitoring site ${station.siteId}, ${station.name}`}
            loading="lazy"
            decoding="async"
            onError={() => setImgFailed(true)}
          />
        </figure>
      )}

      <div className="reading" style={{ background: t.soft }}>
        <div className="reading-head">
          <span className="pill" style={{ background: t.color }}>{t.label}</span>
          {station.latest.year && <span className="reading-when">{station.latest.year}</span>}
        </div>
        <div className="reading-label">Average Secchi depth</div>
        <div className="reading-value" style={{ color: t.color }}>
          {formatValue(station.latest.secchiFt, 1)}
        </div>
        <div className="reading-unit">
          feet
          {station.latest.secchiM !== null &&
            ` · ${formatValue(station.latest.secchiM, 2)} m`}
        </div>
        <p className="reading-blurb">{t.blurb}</p>
      </div>

      <dl className="stats">
        <div>
          <dt>TSI Secchi</dt>
          <dd>{formatValue(station.latest.tsiSecchi, 1)}</dd>
        </div>
        <div>
          <dt>Chlorophyll-a</dt>
          <dd>
            {formatValue(station.latest.chla)}
            <span className="unit">µg/L</span>
          </dd>
        </div>
        <div>
          <dt>Years</dt>
          <dd>{station.years}</dd>
        </div>
      </dl>

      {trendWord && (
        <p className="trendline">
          <span aria-hidden="true">📈</span>
          <span>
            Over {plural(rows.length, "year")} of record this site is{" "}
            <b>{trendWord}</b>
            {trendWord !== "about steady" && (
              <> — Secchi depth is changing by about <b>
                {formatValue(Math.abs(trend as number), 1)} ft per decade</b>
              </>
            )}.
          </span>
        </p>
      )}

      <ShareCard
        url={url}
        filename={station.key}
        heading="Link to this station"
        blurb="Print the code on site signage or a dock sign — it opens this station's record."
      />
    </aside>
  );
}
